import { useEffect, useRef } from "react";

import { colorDelFondo, legibilidad, TONOS } from "@/lib/escalera.mjs";
import { useQuieto } from "@/hooks/useQuieto";

/**
 * El fondo de la portada: UNO, fijo detrás de todo, que cambia de color según dónde estés.
 *
 * DE DÓNDE SALE. Gabri, 8 de octubre de 2026: *«en lugar de una franja de degradado fija en el
 * medio, quiero que el fondo de la página haga una transición de color fluida mediante
 * scroll»*. Antes cada sección pintaba su fondo y entre dos de distinto tono había una franja
 * de degradado. El problema de la franja es que **el cambio ocurre siempre en el mismo sitio
 * del documento**: si te queda a media pantalla y paras, ves media pantalla negra, media clara
 * y la costura en medio.
 *
 * CÓMO FUNCIONA, Y SON TRES LÍNEAS DE IDEA:
 *
 *   1. Cada sección de la portada dice de qué tono es con `data-tono`. Nada más.
 *   2. Esto las busca —una vez, y otra vez si cambia el tamaño— y, al bajar, mira a qué altura
 *      de la ventana está el borde de cada una.
 *   3. Las cuentas están en `lib/escalera.mjs`, sin React y sin navegador: entran posiciones y
 *      sale un color. Aquí sólo se pinta.
 *
 * POR QUÉ `data-tono` Y NO UNAS PROPIEDADES. Porque las secciones de la portada no son hijas
 * de nada común —unas están en `Landing.jsx` y otras son componentes sueltos como el hero o los
 * testimonios— y pasarles un contexto obligaría a tocarlas todas para algo que no es asunto
 * suyo. Un atributo es lo que ya hace el navegador: decir qué eres y que te encuentre quien
 * tenga que encontrarte.
 *
 * Y EL TEXTO NO SE QUEDA ATRÁS, que es la mitad delicada: mientras el fondo pasa de negro a
 * claro, el contenido de la sección que se va se atenúa y el de la que entra aparece. El
 * cálculo también está en la escalera —lo decide el COLOR que hay detrás, no la sección— así
 * que el texto cambia **a la vez** que el fondo y no un renglón después.
 *
 * SI SE PIDE MENOS MOVIMIENTO, no hay mezcla: el fondo salta de un tono al siguiente y nada se
 * atenúa. Quien pide menos movimiento no pide menos contraste.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────
 * Y EL COLOR SE PINTA EN LA PORTADA MISMA —su `<main>`—, QUE ES LA TERCERA VERSIÓN DE ESTO.
 * Las dos anteriores se veían **blancas enteras**, con el texto blanco del hero invisible sobre
 * blanco, y las dos pasaron los porteros y compilaron sin una sola queja:
 *
 *   1. Una capa fija detrás de todo con `z-index: -10`. Los elementos con z negativo se pintan
 *      **antes que los fondos de los bloques del flujo**, así que cualquier fondo del camino se
 *      pinta encima. La capa estaba ahí, con su color bien calculado, y no se veía.
 *   2. El color en el `body`. Mejor, pero tampoco: la aplicación entera vive dentro de un
 *      `<div class="App … bg-white">`, y **ese div tapa el fondo del `body`**.
 *
 * LA LECCIÓN, Y VALE PARA CUALQUIER FONDO: **el fondo lo pinta el elemento más interno que
 * cubre lo que quieres teñir, no el más externo.** Ir hacia fuera —body, html— es ir hacia
 * atrás en el orden de pintado, y cualquier `bg-` del camino gana. Así que esto pinta el
 * `<main>` de la portada, que es el primer elemento por encima de las secciones.
 *
 * Y SE ENCUENTRA SOLO: el componente se monta dentro de ese `<main>` y pinta a su padre. No
 * hace falta decírselo ni acoplarlo a un nombre de clase que mañana cambia.
 *
 * NADA DE ESTO SE VE LEYENDO CÓDIGO, y por eso hay un espejo —`tools/espejo-portada/`— que
 * abre la portada de verdad y la fotografía. Los dos fallos de arriba los cazó Gabri mirando
 * la pantalla; el segundo, el espejo. **Un portero que lee código no ve una página en blanco.**
 *
 * Y NO PISA EL TEMA DE ALTO CONTRASTE: quien lo tiene encendido ha pedido negro y blanco a
 * propósito, y una portada que se pone gris por su cuenta deshace justo lo que pidió.
 */
export default function FondoDeLaPortada() {
  const quieto = useQuieto();

  const ancla = useRef(null);

  useEffect(() => {
    const lienzo = ancla.current?.parentElement;
    if (!lienzo) return undefined;
    if (document.documentElement.dataset.tema === "contraste") return undefined;
    let secciones = [];
    let pedido = null;

    const buscar = () => { secciones = [...document.querySelectorAll("[data-tono]")]; };

    const pintar = () => {
      pedido = null;
      const ventana = window.innerHeight;
      const tramos = secciones.map((s) => ({
        tono: s.dataset.tono,
        arriba: quieto ? (s.getBoundingClientRect().top < ventana / 2 ? -1 : ventana) : s.getBoundingClientRect().top,
      }));
      // Lo que queda de rollo: sin esto, la última sección no termina de entrar nunca y la
      // portada se queda abajo del todo en un color a medio camino.
      const queda = document.documentElement.scrollHeight - window.scrollY - ventana;
      const color = colorDelFondo(tramos, ventana, queda);
      lienzo.style.backgroundColor = color;
      // Y la tinta: el contenido de cada sección vale lo que valga su tono sobre este fondo.
      secciones.forEach((s) => {
        s.style.opacity = quieto ? 1 : legibilidad(color, TONOS[s.dataset.tono]);
      });
    };

    const avisar = () => { if (pedido === null) pedido = requestAnimationFrame(pintar); };

    buscar();
    pintar();
    window.addEventListener("scroll", avisar, { passive: true });
    window.addEventListener("resize", avisar);
    // Las secciones cambian de alto cuando llegan las fuentes o los cursos del servidor, y
    // entonces los bordes que se midieron ya no están donde estaban.
    const ojo = new ResizeObserver(() => { buscar(); avisar(); });
    ojo.observe(document.body);

    return () => {
      window.removeEventListener("scroll", avisar);
      window.removeEventListener("resize", avisar);
      ojo.disconnect();
      if (pedido !== null) cancelAnimationFrame(pedido);
      secciones.forEach((s) => { s.style.opacity = ""; });
      // Y al salir de la portada se devuelve el fondo a la hoja de estilos: el resto de la
      // casa es blanca, y una pantalla que se queda negra al navegar es un fallo que sólo se
      // ve al salir.
      lienzo.style.backgroundColor = "";
    };
  }, [quieto]);

  // NO PINTA NADA SUYO: pinta el fondo de quien lo contiene. Lo único que deja en la página es
  // esta marca sin tamaño, que es cómo encuentra a su padre sin que nadie se lo diga.
  return <span ref={ancla} aria-hidden="true" className="hidden" data-testid="fondo-de-la-portada" />;
}
